/**
 * translations.ts — the compiled-in baseline for every UI string.
 *
 * The copy itself lives in `src/locales/<language>.json` (flat key → string,
 * `{token}` placeholders). Three reasons the strings are data files rather
 * than a TypeScript object literal:
 *
 *  1. Translators (and the admin copy editor) work on JSON, not on a 1,100-line
 *     source file guarded by a build.
 *  2. The DB override layer (`public.content_translations`, surfaced by
 *     `useContentOverrides`) stores exactly the same shape, so a string can move
 *     between "shipped default" and "admin-edited" without changing form.
 *  3. Nothing in a locale bundle can be computed any more. Values that used to
 *     be built from `DEFAULT_COUNTRY` at import time (the support phone, the
 *     country name, the example cities) are now `{supportPhone}` / `{country}` /
 *     `{city1}` tokens resolved at RENDER time — see src/lib/contentTokens.ts.
 *     That is what lets one bundle serve an admin-configured country, and it
 *     keeps country literals out of the copy (issue #378 ratchet).
 *
 * NOTE: 'en-UG' below is the translation-dictionary KEY (the Language union in
 * useLanguage), not a country literal — renaming it would break stored user
 * language preferences.
 */
import type { Language } from '@/hooks/useLanguage';
import enUG from '@/locales/en-UG.json';
import enUS from '@/locales/en-US.json';
import fr from '@/locales/fr.json';
import sw from '@/locales/sw.json';
import ar from '@/locales/ar.json';

/** The complete dictionary every other language falls back to, key by key. */
export const BASE_LANGUAGE: Language = 'en-UG';

export const translations: Record<Language, Record<string, string>> = {
  'en-UG': enUG,
  'en-US': enUS,
  fr,
  sw,
  ar,
};

export type TranslationKey = keyof typeof enUG;

/** Every key the app ships, in the order the base bundle declares them. */
export const TRANSLATION_KEYS = Object.keys(enUG) as TranslationKey[];
